import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '@/hooks/useAuth';
import { SignUpInput, SignInInput } from '@/types/auth';

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  signIn: (input: SignInInput) => Promise<boolean>;
  signUp: (input: SignUpInput) => Promise<boolean>;
  signOut: () => Promise<boolean>;
  isConfirmationPending: boolean;  // 追加
  pendingEmail: string | null;     // 追加
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuthContext() { 
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isConfirmationPending, setIsConfirmationPending] = useState(false);  // 追加
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);      // 追加
  const { error: authError, signIn, signUp, signOut } = useAuth();

  // 認証状態のチェックは、確認待ちでない場合のみ行う
  useEffect(() => {
    const checkAuth = async () => {
      if (isConfirmationPending) {
        setLoading(false);
        return;
      }

      try {
        const session = await fetchAuthSession();
        setIsAuthenticated(!!session.tokens);
      } catch {
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [isConfirmationPending]);

  const value = {
    isAuthenticated,
    loading: loading && !isConfirmationPending, // 確認待ち時はローディングを表示しない
    error: authError,
    isConfirmationPending,  // 追加
    pendingEmail,           // 追加
    signIn: async (input: SignInInput) => {
      const success = await signIn(input);
      if (success) {
        setIsAuthenticated(true);
        setIsConfirmationPending(false);
        setPendingEmail(null);
      }
      return success;
    },
    signUp: async (input: SignUpInput) => {
      const needsConfirmation = await signUp(input);
      if (needsConfirmation) {
        setIsConfirmationPending(true);
        setPendingEmail(input.email);
        setIsAuthenticated(false);
      }
      return needsConfirmation;
    },
    signOut: async () => {
      const success = await signOut();
      if (success) {
        setIsAuthenticated(false);
        setIsConfirmationPending(false);
        setPendingEmail(null);
      }
      return success;
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
